// Thin fetch wrapper — handles auth, error extraction, JSON parsing.
//
// FastAPI returns `detail` in 3 shapes:
//   1. HTTPException(detail="...")    → string
//   2. Pydantic validation error      → array of {loc, msg, type}
//   3. Some middleware                → object {message}
// JS template-literals stringify shapes 2+3 as "[object Object]" — this
// normalizes all three into a human-readable string.
export function extractErrorMessage(payload, status) {
  if (!payload) return `HTTP ${status}`;
  const d = payload.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d.map((e) => {
      const loc = Array.isArray(e?.loc) ? e.loc.slice(1).join(".") : "";
      const msg = e?.msg || e?.message || JSON.stringify(e);
      return loc ? `${loc}: ${msg}` : msg;
    }).join("; ");
  }
  if (d && typeof d === "object") {
    return d.message || d.error || JSON.stringify(d);
  }
  return payload.message || payload.error || `HTTP ${status}`;
}

// --- Console log styling -------------------------------------------------
// Two color tracks so the user can scan the devtools console:
//   →  request   (blue, bold)
//   ←  response  (green if 2xx, red if 4xx/5xx)
//   ✗  network/parse error (red bold)
// Method is left-padded to 6 chars so URLs line up vertically.
const _STYLE = {
  req:   "color: #60a5fa; font-weight: 600",
  ok:    "color: #4cd197; font-weight: 600",
  bad:   "color: #ff8585; font-weight: 600",
  fail:  "color: #ff8585; font-weight: 700",
  dim:   "color: #888",
  reset: "color: inherit; font-weight: normal",
};

function _logRequest(method, url, body) {
  const tag = `${method.padEnd(6)} ${url}`;
  if (body && !(body instanceof FormData)) {
    console.log(`%c→ ${tag}`, _STYLE.req, body);
  } else if (body instanceof FormData) {
    console.log(`%c→ ${tag} %c(FormData)`, _STYLE.req, _STYLE.dim);
  } else {
    console.log(`%c→ ${tag}`, _STYLE.req);
  }
}

function _logResponse(method, url, status, ms, payload) {
  const tag = `${method.padEnd(6)} ${url}`;
  const ok = status >= 200 && status < 300;
  const style = ok ? _STYLE.ok : _STYLE.bad;
  // Verbose payloads (image trees, jobs lists) flood the console — only
  // log payload for errors. For 2xx we just log the timing line.
  if (ok) {
    console.log(`%c← ${status} ${tag} %c(${ms}ms)`, style, _STYLE.dim);
  } else {
    console.log(`%c← ${status} ${tag} %c(${ms}ms)`, style, _STYLE.dim, payload);
  }
}

function _logNetError(method, url, ms, err) {
  const tag = `${method.padEnd(6)} ${url}`;
  console.log(`%c✗ ${tag} %c(${ms}ms) — ${err.message}`, _STYLE.fail, _STYLE.dim);
}

async function _request(method, url, opts = {}) {
  const init = { method, cache: "no-store", ...opts };
  // Auto-attach JSON content-type when body is a plain object.
  if (init.body && typeof init.body === "object" && !(init.body instanceof FormData)) {
    init.headers = { "content-type": "application/json", ...(init.headers || {}) };
    init.body = JSON.stringify(init.body);
  }
  // Log the original body (object or FormData), not the JSON-encoded string.
  _logRequest(method, url, opts.body);

  const t0 = performance.now();
  let res;
  try {
    res = await fetch(url, init);
  } catch (netErr) {
    _logNetError(method, url, Math.round(performance.now() - t0), netErr);
    throw netErr;
  }
  const ms = Math.round(performance.now() - t0);
  const text = await res.text();
  let payload;
  try { payload = text ? JSON.parse(text) : null; }
  catch { payload = { raw_text: text }; }
  _logResponse(method, url, res.status, ms, payload);

  if (!res.ok) {
    const msg = extractErrorMessage(payload, res.status);
    const err = new Error(msg);
    err.status = res.status;
    err.payload = payload;
    throw err;
  }
  return payload;
}

export const api = {
  get:    (url)        => _request("GET",    url),
  post:   (url, body)  => _request("POST",   url, { body }),
  put:    (url, body)  => _request("PUT",    url, { body }),
  delete: (url)        => _request("DELETE", url),
  // FormData POST — used for file uploads (no JSON headers).
  postForm: (url, formData) => _request("POST", url, { body: formData }),
};

// --- Specific endpoint wrappers ----------------------------------------

export const keysApi = {
  list:        ()             => api.get("/api/admin/keys"),
  add:         (label, key)   => api.post("/api/admin/keys", { label, key }),
  update:      (id, patch)    => api.put(`/api/admin/keys/${id}`, patch),
  remove:      (id)           => api.delete(`/api/admin/keys/${id}`),
  refreshOne:  (id)           => api.post(`/api/admin/keys/${id}/refresh-usage`),
  refreshAll:  ()             => api.post("/api/admin/keys/refresh-all-usage"),
  history:     (id, page=1, pageSize=20) =>
                                 api.post("/api/admin/usage/histories", {
                                   key_id: id, page, page_size: pageSize,
                                 }),
  historyDefault: (page=1, pageSize=20) =>
                                 api.post("/api/admin/usage/histories", {
                                   page, page_size: pageSize,
                                 }),
};

export const jobsApi = {
  get:    (id)            => api.get(`/api/jobs/${id}`),
  list:   (limit=100)     => api.get(`/api/jobs?limit=${limit}`),
  submit: (formData)      => api.postForm("/api/jobs", formData),
  cancel: (id)            => api.post(`/api/jobs/${id}/cancel`),
};

export const filesApi = {
  // List ALL job dirs under output/ with rolled-up image counts + sizes.
  // Used by FilesModal as the master pane.
  listJobs:    ()         => api.get("/api/admin/files"),
  // Drill-down: list all user-facing images in one job dir.
  listJobFiles: (jobId)   => api.get(`/api/admin/files/${encodeURIComponent(jobId)}`),
};

export const healthApi = {
  check: () => api.get("/api/health"),
};

// Per-key prompt overrides — each ShopAIKey id has its own bucket. Pass
// `keyId="default"` (or omit) to target the fallback bucket used by the
// .env key and any job that didn't bind a specific key.
function _kq(keyId) {
  if (!keyId) return "";
  return `?key_id=${encodeURIComponent(keyId)}`;
}
export const promptsApi = {
  list:   (keyId)         => api.get(`/api/admin/prompts${_kq(keyId)}`),
  update: (key, value, keyId) =>
                             api.put(`/api/admin/prompts/${key}${_kq(keyId)}`, { value }),
  reset:  (key, keyId)    => api.delete(`/api/admin/prompts/${key}${_kq(keyId)}`),
};

// CC preview layout editor — global (single-instance) grid config that
// the pipeline substitutes into the cc_preview prompt on every job.
// PUT accepts a partial patch; server merges into current + validates.
export const ccPreviewLayoutApi = {
  get:    ()              => api.get("/api/admin/cc_preview_layout"),
  update: (patch)         => api.put("/api/admin/cc_preview_layout", patch),
  reset:  ()              => api.post("/api/admin/cc_preview_layout/reset"),
};
